import React, { useState } from 'react';
import { Loader2, Save, Building2, User, Target, CalendarClock, StickyNote } from 'lucide-react';
import { createProspect, updateProspect } from '../services/prospectService';
import {
    STATUTS, STATUT_MAP, SOURCES, TYPES_ETABLISSEMENT,
    ACTIONS_SUGGESTIONS, PROCHAINES_ACTIONS_SUGGESTIONS,
    OBJECTIONS, interetLevel, toInputDate
} from '../config/prospects';

const inputCls = 'w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition';
const labelCls = 'block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5';

export const addDays = (n) => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    return toInputDate(d);
};

export const QuickDates = ({ onPick }) => (
    <div className="flex flex-wrap gap-1.5 mt-2">
        {[
            { label: "Aujourd'hui", days: 0 },
            { label: 'Demain', days: 1 },
            { label: '+3 jours', days: 3 },
            { label: '+1 semaine', days: 7 },
            { label: '+2 semaines', days: 14 }
        ].map(q => (
            <button
                key={q.label}
                type="button"
                onClick={() => onPick(addDays(q.days))}
                className="px-2 py-1 text-[10px] font-bold text-slate-600 bg-slate-100 hover:bg-blue-50 hover:text-blue-700 rounded-md transition"
            >
                {q.label}
            </button>
        ))}
    </div>
);

const Section = ({ icon, title, children }) => (
    <div className="space-y-4">
        <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.15em] text-slate-900">
            <span className="text-blue-600">{icon}</span>
            {title}
        </div>
        {children}
    </div>
);

const emptyForm = {
    etablissement: '',
    typeEtablissement: '',
    ville: '',
    contact: '',
    telephone: '',
    email: '',
    source: 'recherche',
    statut: 'a_contacter',
    interet: 0,
    derniereAction: '',
    prochaineAction: '',
    dateProchaineAction: '',
    responsable: '',
    notes: '',
    objection: ''
};

const ProspectForm = ({ prospect, responsables = [], onSaved, onCancel }) => {
    const isEdit = Boolean(prospect?._id);
    const [form, setForm] = useState(() => (
        isEdit
            ? { ...emptyForm, ...prospect, dateProchaineAction: toInputDate(prospect.dateProchaineAction) }
            : { ...emptyForm }
    ));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    // When the status changes, suggest the matching interest level
    // only if the user hasn't customised it yet.
    const handleStatutChange = (statut) => {
        setForm(prev => {
            const prevDefault = STATUT_MAP[prev.statut]?.interet ?? 0;
            const untouched = Number(prev.interet) === prevDefault;
            return {
                ...prev,
                statut,
                interet: untouched ? (STATUT_MAP[statut]?.interet ?? prev.interet) : prev.interet
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.etablissement.trim()) {
            setError("Le nom de l'établissement est obligatoire.");
            return;
        }
        setSaving(true);
        setError('');
        try {
            const payload = {
                etablissement: form.etablissement,
                typeEtablissement: form.typeEtablissement,
                ville: form.ville,
                contact: form.contact,
                telephone: form.telephone,
                email: form.email,
                source: form.source,
                statut: form.statut,
                interet: Number(form.interet) || 0,
                derniereAction: form.derniereAction,
                prochaineAction: form.prochaineAction,
                dateProchaineAction: form.dateProchaineAction || null,
                responsable: form.responsable,
                notes: form.notes
            };
            const saved = isEdit
                ? await updateProspect(prospect._id, payload)
                : await createProspect(payload);
            onSaved?.(saved, isEdit);
        } catch (err) {
            setError(err.response?.data?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSaving(false);
        }
    };

    const level = interetLevel(Number(form.interet));

    return (
        <form onSubmit={handleSubmit} className="space-y-8">
            {/* Établissement */}
            <Section icon={<Building2 size={14} />} title="Établissement">
                <div>
                    <label className={labelCls}>Client / Établissement *</label>
                    <input
                        autoFocus={!isEdit}
                        className={inputCls}
                        value={form.etablissement}
                        onChange={e => set('etablissement', e.target.value)}
                        placeholder="Ex : École Les Pionniers"
                    />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelCls}>Type</label>
                        <input
                            list="prospect-types"
                            className={inputCls}
                            value={form.typeEtablissement}
                            onChange={e => set('typeEtablissement', e.target.value)}
                            placeholder="École privée, Centre…"
                        />
                        <datalist id="prospect-types">
                            {TYPES_ETABLISSEMENT.map(t => <option key={t} value={t} />)}
                        </datalist>
                    </div>
                    <div>
                        <label className={labelCls}>Ville</label>
                        <input className={inputCls} value={form.ville} onChange={e => set('ville', e.target.value)} placeholder="Ex : Sfax" />
                    </div>
                </div>
            </Section>

            {/* Contact */}
            <Section icon={<User size={14} />} title="Contact">
                <div>
                    <label className={labelCls}>Personne de contact</label>
                    <input className={inputCls} value={form.contact} onChange={e => set('contact', e.target.value)} placeholder="Ex : Directeur, Mme Amel…" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelCls}>Téléphone</label>
                        <input className={inputCls} value={form.telephone} onChange={e => set('telephone', e.target.value)} placeholder="98 123 456" />
                    </div>
                    <div>
                        <label className={labelCls}>Email</label>
                        <input type="email" className={inputCls} value={form.email} onChange={e => set('email', e.target.value)} placeholder="contact@ecole.tn" />
                    </div>
                </div>
            </Section>

            {/* Suivi */}
            <Section icon={<Target size={14} />} title="Où on en est ?">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelCls}>Source / Canal</label>
                        <select className={inputCls} value={form.source} onChange={e => set('source', e.target.value)}>
                            {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelCls}>Statut</label>
                        <select className={inputCls} value={form.statut} onChange={e => handleStatutChange(e.target.value)}>
                            {STATUTS.map(s => <option key={s.value} value={s.value}>{s.emoji} {s.label}</option>)}
                        </select>
                    </div>
                </div>
                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <label className={labelCls + ' mb-0'}>Niveau d'intérêt</label>
                        <span className={`text-xs font-black ${level.text}`}>{form.interet}% · {level.label}</span>
                    </div>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        step="10"
                        value={form.interet}
                        onChange={e => set('interet', Number(e.target.value))}
                        className="w-full accent-blue-600"
                    />
                    <div className="flex justify-between text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-1">
                        <span>Faible</span><span>Moyen</span><span>Intéressé</span><span>Presque client</span>
                    </div>
                </div>
            </Section>

            {/* Actions */}
            <Section icon={<CalendarClock size={14} />} title="Qu'a-t-on fait ? → Que faire ensuite ? → Quand ?">
                <div>
                    <label className={labelCls}>Dernière action</label>
                    <input
                        list="prospect-actions"
                        className={inputCls}
                        value={form.derniereAction}
                        onChange={e => set('derniereAction', e.target.value)}
                        placeholder="Email envoyé, Appel effectué…"
                    />
                    <datalist id="prospect-actions">
                        {ACTIONS_SUGGESTIONS.map(a => <option key={a} value={a} />)}
                    </datalist>
                    {isEdit && (
                        <p className="text-[10px] text-slate-400 mt-1">Astuce : utilisez « Nouvelle action » pour garder l'historique avec une note.</p>
                    )}
                </div>
                <div>
                    <label className={labelCls}>Prochaine action</label>
                    <input
                        list="prospect-next-actions"
                        className={inputCls}
                        value={form.prochaineAction}
                        onChange={e => set('prochaineAction', e.target.value)}
                        placeholder="Relancer par téléphone, Programmer démo…"
                    />
                    <datalist id="prospect-next-actions">
                        {PROCHAINES_ACTIONS_SUGGESTIONS.map(a => <option key={a} value={a} />)}
                    </datalist>
                </div>
                <div>
                    <label className={labelCls}>Date prochaine action</label>
                    <input
                        type="date"
                        className={inputCls}
                        value={form.dateProchaineAction}
                        onChange={e => set('dateProchaineAction', e.target.value)}
                    />
                    <QuickDates onPick={v => set('dateProchaineAction', v)} />
                </div>
            </Section>

            {/* Responsable, objection & notes */}
            <Section icon={<StickyNote size={14} />} title="Responsable, objection & notes">
                <div>
                    <label className={labelCls}>Responsable</label>
                    <input
                        list="prospect-responsables"
                        className={inputCls}
                        value={form.responsable}
                        onChange={e => set('responsable', e.target.value)}
                        placeholder="Ex : Brahim"
                    />
                    <datalist id="prospect-responsables">
                        {responsables.map(r => <option key={r} value={r} />)}
                    </datalist>
                </div>
                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <label className={labelCls + ' mb-0'}>Objection principale / Frein rencontré</label>
                        {form.objection && (
                            <button
                                type="button"
                                onClick={() => set('objection', '')}
                                className="text-[10px] text-red-500 hover:text-red-700 font-bold"
                            >
                                Effacer
                            </button>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {OBJECTIONS.map(obj => {
                            const isSelected = form.objection === obj.label;
                            return (
                                <button
                                    key={obj.value}
                                    type="button"
                                    onClick={() => set('objection', isSelected ? '' : obj.label)}
                                    className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-lg border transition ${
                                        isSelected
                                            ? 'bg-red-600 text-white border-red-600 shadow-xs'
                                            : 'bg-white text-slate-700 border-red-200 hover:bg-red-50'
                                    }`}
                                >
                                    <span>{obj.emoji}</span>
                                    <span>{obj.label}</span>
                                </button>
                            );
                        })}
                    </div>
                    <input
                        type="text"
                        className={inputCls}
                        value={form.objection}
                        onChange={e => set('objection', e.target.value)}
                        placeholder="Ex : Prix jugé élevé, Déjà équipé de X..."
                    />
                </div>
                <div>
                    <label className={labelCls}>Notes / Synthèse</label>
                    <textarea
                        rows={3}
                        className={inputCls + ' resize-y'}
                        value={form.notes}
                        onChange={e => set('notes', e.target.value)}
                        placeholder="Besoin spécifique, détails de la négociation..."
                    />
                </div>
            </Section>

            {error && (
                <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-xs font-bold rounded-lg">{error}</div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg transition"
                >
                    Annuler
                </button>
                <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-5 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800 disabled:opacity-60 transition shadow-sm"
                >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    {isEdit ? 'Enregistrer' : 'Créer le prospect'}
                </button>
            </div>
        </form>
    );
};

export default ProspectForm;
